
import React, { Component, useEffect, useMemo, useState } from "react";
import WebDesigner from "./WebDesigner";

import LayoutNode, { getNodeStyleAttributeValue, setNodeComponentAttributeValue, setNodeStyleAttributeValue } from "vagaro-website-rendering/src/components/Definitions/LayoutNode";
import { GetWBComponentByTypename } from "./Data/WBData";
import { GetGridAreaDataFromNode, GetGridRenderData, GridAreaData } from "./Utilities/GridUtils";
import RenderNodeRecursive from "./Utilities/RenderNodeRecursive";
import { gridAttributes } from "vagaro-website-rendering/src/components/Definitions/AttributeTypes";


type DragProps = {
  draggedObject: LayoutNode | null;
  mouseX: number;
  mouseY: number;
  wbManager: WebDesigner;
  gridObject?: LayoutNode;
  gridDom?: HTMLElement;
  inGrid?: boolean;
};

function DraggedComponent({ draggedObject, mouseX, mouseY, wbManager, gridObject, gridDom, inGrid }: DragProps) {
  const [position, setPosition] = useState({ mouseX, mouseY });

  useEffect(() => {
    wbManager.draggedRenderComponent = {
      setState: setPosition
    };

  }, []);


  if (!draggedObject) return null;

  const comp = GetWBComponentByTypename(draggedObject.typename);
  if (!comp) return null;

  if (inGrid && gridObject && gridDom) {

    return <GridDragger draggedObject={draggedObject} mouseX={mouseX} mouseY={mouseY} wbManager={wbManager} gridObject={gridObject} gridDom={gridDom} inGrid={inGrid} position={position} />


  }


  return (
    <div
      className="dragging"
      style={{ left: position.mouseX + "px", top: position.mouseY + "px" }}
    >
      <div className="component-slot dragged">
        {comp.icon}
        {comp.displayName}
      </div>
    </div>
  );



};


function GridDragger({ draggedObject, wbManager, gridObject, gridDom, inGrid, position }: DragProps & { position: { mouseX: number, mouseY: number } }) {

  const [toggle, setToggle] = useState(false);

  const otherTargets = useMemo(
    () => {

      if (!draggedObject?.parent)
        return [];

      return wbManager.state.selectedTargets.filter(t => t.layout.parent?.id == gridObject?.id && t.layout.id != draggedObject?.id).map(l => { return { dom: l.dom, layout: l.layout, area: GetGridAreaDataFromNode(l.layout, wbManager.state.screenSize) } })
    }, [draggedObject, gridObject]

  );

  useEffect(() => { //since the grid moves around we need to update the position after animation has finished, trigger a render to get the new position after 400ms
    setTimeout(() => {
      setToggle(!toggle);


    }, 400);

  }, []);

  const initGridRender = useMemo(() => {


    if (!inGrid || !gridObject || !gridDom) {
      return null;
    }

    return GetGridRenderData(gridObject, wbManager.state.screenSize);


  }, [gridObject, gridDom, inGrid]);

  const initGridArea: GridAreaData | null = useMemo(() => {

    if (!gridDom || !initGridRender || !draggedObject) {
      return null;
    }

    if (draggedObject.parent === gridObject) {
      return GetGridAreaDataFromNode(draggedObject, wbManager.state.screenSize);
    }

    let bounds = wbManager.draggedStartBounds;

    let gridBounds = gridDom.getBoundingClientRect();
    let gridWidht = Math.floor(gridBounds.width / initGridRender.colCount);
    let gridHeight = Math.floor(gridBounds.height / initGridRender.rowCount);

    return {
      xStart: 0,
      yStart: 0,
      xEnd: 0,
      yEnd: 0,
      width: Math.round((bounds.width) / gridWidht),
      height: Math.round((bounds.height) / gridHeight),
    }


  }, [draggedObject, gridObject]);

  if (!gridDom || !gridObject || !draggedObject) {
    return null;
  }

  if (!initGridArea) {
    return null;
  }





  let area = initGridArea;

  let gridInfo = GetGridRenderData(gridObject, wbManager.state.screenSize);
  let gridBounds = gridDom.getBoundingClientRect();
  let gridGap = 0// gridInfo.gap/gridBounds.width;

  let cellWidth = (gridBounds.width) / gridInfo.colCount;
  let cellHeight = (gridBounds.height) / gridInfo.rowCount;

  let pixelGapX = cellWidth * gridInfo.gapX / 100;
  let pixelGapY = cellHeight * gridInfo.gapY / 100 / (gridInfo.ratio||1);
  let halfWidth = area.width * cellWidth / 2;
  let halfHeight = area.height * cellHeight / 2;
  let mouseOffsetX = wbManager.draggedOffset.x;
  let mouseOffsetY = wbManager.draggedOffset.y;
  if (draggedObject.parent == null) {

    mouseOffsetX = -halfWidth;
    mouseOffsetY = -halfHeight;
  }
  let x = position.mouseX + mouseOffsetX + halfWidth;
  let y = position.mouseY + mouseOffsetY + halfHeight;

  let xPoint = x - (gridBounds.x + gridGap / 2) - halfWidth + cellWidth / 2;
  let yPoint = y - (gridBounds.y + gridGap / 2) - halfHeight + cellHeight / 2;
  let xStart = Math.floor(xPoint / cellWidth);
  let yStart = Math.floor(yPoint / cellHeight);

  let yEnd = yStart + area.height;
  let xEnd = xStart + area.width;



  let realX = xStart * cellWidth + gridBounds.x;
  let realY = yStart * cellHeight + gridBounds.y;

  let hght = area.height * cellHeight;
  let wdt = area.width * cellWidth;

  let str = `${Math.max(yStart + 1, 1)}/${Math.max(xStart + 1, 1)}/${Math.min(yEnd + 1,(initGridRender?.rowCount??10)+1)}/${Math.min(xEnd + 1, (initGridRender?.colCount ?? 10) + 1)}`;


  if (str != wbManager.currentGridArea) {
    //temporarilly update the node value so the UI can show the correct area,
    wbManager.currentGridArea = str;
    //let getVal = getNodeStyleAttributeValue(draggedObject, wbManager.state.screenSize, gridAttributes.gridArea, "");

    //setNodeStyleAttributeValue(draggedObject, wbManager.state.screenSize, gridAttributes.gridArea, str);
    if ((window as any).updateGridAreaUI) {

      (window as any).updateGridAreaUI(str);
    }
    //revert to initial state so that dragging in and out maintains the same area
    // setNodeStyleAttributeValue(draggedObject, wbManager.state.screenSize, gridAttributes.gridArea, getVal);
  }



  // const initRowCount = initGridRender?.rowCount ?? 0;

  if (realX < gridBounds.x) {
    let dif = gridBounds.x - realX;
    realX = gridBounds.x;
    wdt -= dif;
  }
  if (realY < gridBounds.y) {
    let dif = gridBounds.y - realY;
    realY = gridBounds.y;
    hght -= dif;
  }
  if (realX + wdt > gridBounds.x + gridBounds.width - gridGap) {
    let dif = realX + wdt - (gridBounds.x + gridBounds.width - gridGap);
    wdt -= dif;
  }
  if (realY + hght > gridBounds.y + gridBounds.height - gridGap) {
    let dif = realY + hght - (gridBounds.y + gridBounds.height - gridGap);
    hght -= dif;
  }

  let yDif = yStart - area.yStart;
  let xDif = xStart - area.xStart;



  if (otherTargets.length > 0) {
    for (let i = 0; i < otherTargets.length; i++) {
      let target = otherTargets[i].layout;
      let targetArea = otherTargets[i].area;
      // if (targetArea.yEnd + yDif > lowestY) {
      //   lowestY = targetArea.yEnd + yDif;
      // }
      let areaStr = `${Math.max(targetArea.yStart + yDif + 1, 1)}/${Math.max(targetArea.xStart + xDif + 1, 1)}/${Math.min(targetArea.yEnd + yDif + 1)}/${Math.min(targetArea.xEnd + xDif + 1, (initGridRender?.colCount ?? 10) + 1)}`
      setNodeStyleAttributeValue(target, wbManager.state.screenSize, "gridArea", areaStr);
      otherTargets[i].dom.style.gridArea = areaStr;
    }

  }
  //let row =  realY + hght - (gridBounds.y + gridBounds.height)


  // if (lowestY > gridInfo.rowCount) {
  //   setTimeout(() => {
  //     setNodeStyleAttributeValue(gridObject, wbManager.state.screenSize, "gridTemplateRows", `repeat(${lowestY},1fr)`);
  //     gridObject.forceUpdate && gridObject.forceUpdate();
  //   }, 0);


  // }

  // else if (gridInfo.rowCount > initRowCount && lowestY < gridInfo.rowCount) {
  //   let newRow = Math.max(initRowCount, lowestY);
  //   setTimeout(() => {
  //     setNodeStyleAttributeValue(gridObject, wbManager.state.screenSize, "gridTemplateRows", `repeat(${newRow},1fr)`);
  //     gridObject.forceUpdate && gridObject.forceUpdate()
  //   }, 0);

  // }



  return <div className="v-site grid-dragging-wrapper" style={{fontSize:"16px",zIndex:50}} ><div
    className="in-grid-dragging hover-outline"
    style={{

      pointerEvents: "none",
      position: "fixed",
      left: realX + pixelGapX / 2 + "px",
      top: realY + pixelGapY / 2 + "px",
      width: wdt - pixelGapX + "px",
      height: hght - pixelGapY + "px",
      backgroundColor: "none",
      // border: "2px dashed green",
      opacity: "0.75",
      fontSize:  gridDom.style.fontSize??"",
      fontFamily: gridDom.style.fontFamily??"",
      color: gridDom.style.color??"",
      fontStyle: gridDom.style.fontStyle??"",
      fontWeight: gridDom.style.fontWeight??"",


    }}

  >
    <div className="w-full h-full" style={{position:"absolute",top:"0px",left:"0px",backgroundColor:"black",opacity:"0.075",borderRadius:"4px"}}>

    </div>
    <RenderNodeRecursive
      node={draggedObject}
      screenSize={wbManager.state.screenSize}
      siteData={wbManager.state.siteData}
      userContext={wbManager.state.siteData.userContext}
    />

<div className="dot t"></div>
<div className="dot r"></div>
<div className="dot l"></div>
<div className="dot b"></div>
  </div>
  </div>

}

export default DraggedComponent;